<script setup>
import { Head } from "@inertiajs/vue3";
import { onMounted, ref, computed } from "vue";
import { useDarkMode } from "@/stores/darkMode.js"
import { useLang } from "@/stores/lang.js"
import { VSonner } from "vuetify-sonner";

const darkStore = useDarkMode();
const props = defineProps({
    option: {
        type: Object,
        default: () => ({}),
    },
    category: {
        type: [Object, Array],
        default: () => [],
    },
    languages: {
        type: Object,
        default: () => ({}),
    },
});

const localeLang = useLang();

const currentOption = computed(() => {
    return props.option?.[localeLang.myPrefix] || props.option?.ku || props.option?.ckb || {};
});

const siteTitle = computed(() => {
    return currentOption.value?.title || 'KhalMenu';
});

const siteAbout = computed(() => {
    const about = currentOption.value?.about || currentOption.value?.min_about || props.option?.ku?.about || '';
    return typeof about === 'string' ? about.slice(0, 160) : '';
});

const logoUrl = computed(() => {
    const logo = props.option?.ku?.logo || currentOption.value?.logo || 'logo.png';
    return `/assets/images/${logo}`;
});

const headerImageUrl = computed(() => {
    const img = props.option?.ku?.header_image || currentOption.value?.header_image || '';
    return img ? `/assets/images/${img}` : logoUrl.value;
});

const keywordsList = computed(() => {
    if (!props.category) return siteTitle.value;
    if (Array.isArray(props.category)) {
        return props.category.map(item => item[`name_${localeLang.myPrefix}`] || item.name_ku || item.name || '').filter(Boolean).join(', ');
    }
    return siteTitle.value;
});

const structuredData = computed(() => {
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Restaurant",
        "name": siteTitle.value,
        "description": siteAbout.value,
        "image": headerImageUrl.value,
        "telephone": currentOption.value?.phone1 || currentOption.value?.phone2 || undefined,
        "address": currentOption.value?.address ? {
            "@type": "PostalAddress",
            "streetAddress": currentOption.value.address
        } : undefined,
        "url": typeof window !== 'undefined' ? window.location.origin : undefined
    });
});
</script>
<template>
    <Head>
        <title>{{ siteTitle }}</title>
        <meta head-key="description" name="description" :content="siteAbout" />
        <meta name="keywords" :content="keywordsList" />
        <meta name="robots" content="index, follow" />
        <link rel="icon" type="image/png" :href="logoUrl" />
        <link v-if="headerImageUrl" rel="preload" as="image" :href="headerImageUrl" fetchpriority="high" />
        <link v-if="logoUrl" rel="preload" as="image" :href="logoUrl" />

        <!-- Open Graph / Facebook -->
        <meta property="og:type" content="website" />
        <meta property="og:title" :content="siteTitle" />
        <meta property="og:description" :content="siteAbout" />
        <meta property="og:image" :content="headerImageUrl" />
        <meta property="og:site_name" :content="siteTitle" />

        <!-- Twitter -->
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" :content="siteTitle" />
        <meta name="twitter:description" :content="siteAbout" />
        <meta name="twitter:image" :content="headerImageUrl" />

        <!-- Structured Data -->
        <component :is="'script'" type="application/ld+json">
            {{ structuredData }}
        </component>
    </Head>
    <v-app :theme="darkStore.darkMode">
        <VSonner :duration="6000" :visible-toasts="5" position="top-left" expand />
        <slot></slot>
    </v-app>
</template>
<script>
export default {
    data() {
        return {
            dialog: false,
        }
    },
}
</script>
<style>
.footer-link {
    display: block;
    text-align: start;

    transition: all .3s;
    padding: 5px;
}

.footer-link:hover {
    padding: 5px 10px;
    margin-inline-start: 3px;
}

.v-input--horizontal .v-input__append {
    margin-inline-start: 0px;
}

@media print {
    .v-toolbar__content {
        max-width: 1800px !important;
        justify-content: center;
        margin: auto;
    }
}
</style>
